import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../ui/accordion";
import { supabase } from "../../../lib/supabase";
import { LOTTERY_TYPES } from "../../../config/lotteryConfig";
import LotteryNumlotIndexDetailTemplate from "./LotteryNumlotIndexDetailTemplate";
import LotteryNumlotWinningNumbers from "../LotteryNumlotWinningNumbers";
import AdBanner from "../../ads/AdBanner";
import { Badge } from "../../ui/badge";
import { isNew } from "../../../utils/common/format";

export default function LotteryNumlotIndexSummaryTemplate({
  items,
  title,
  lotteryPath,
  lotteryKey,
  hasMore,
  loading,
  onMore,
  showMore,
  fontSize,
}) {
  const [openRounds, setOpenRounds] = useState([]);
  const [detailCache, setDetailCache] = useState({});
  const [loadingRounds, setLoadingRounds] = useState({});
  const [analyzedUuids, setAnalyzedUuids] = useState(new Set());

  const config = LOTTERY_TYPES[lotteryKey];

  // AI解析済みuuidセットを取得
  useEffect(() => {
    if (!items.length) return;
    const uuids = items.map(item => item.uuid).filter(Boolean);
    if (!uuids.length) return;

    const fetchAnalyzed = async () => {
      const { data } = await supabase
        .schema('chatorahack')
        .from('lottery_numlot_analysis_ai')
        .select('lottery_uuid')
        .in('lottery_uuid', uuids);
      if (data) {
        setAnalyzedUuids(new Set(data.map(d => d.lottery_uuid)));
      }
    };
    fetchAnalyzed();
  }, [items]);

  const handleValueChange = async (values) => {
    const newRound = values.find(
      (v) => !openRounds.includes(v) && !detailCache[v]
    );
    setOpenRounds(values);
    if (!newRound) return;

    setLoadingRounds((prev) => ({ ...prev, [newRound]: true }));

    try {
      const targetItem = items.find(item => String(item.round) === newRound);

      const [detailRes, aiRes] = await Promise.all([
        supabase
          .schema("chatorahack")
          .rpc("get_lottery_numlot_detail", {
            p_lottery_key: lotteryKey,
            p_round: parseInt(newRound, 10),
          }),
        targetItem?.uuid
          ? supabase
              .schema('chatorahack')
              .from('lottery_numlot_analysis_ai')
              .select('result')
              .eq('lottery_uuid', targetItem.uuid)
              .order('updated_at', { ascending: false })
              .limit(1)
              .single()
          : Promise.resolve({ data: null }),
      ]);

      if (detailRes.error) throw detailRes.error;
      const row = detailRes.data[0];
      if (!row) throw new Error("データなし");

      let aiSummary = null;
      if (aiRes.data?.result) {
        const result = typeof aiRes.data.result === 'string'
          ? JSON.parse(aiRes.data.result)
          : aiRes.data.result;
        aiSummary = result.summary || null;
      }

      setDetailCache((prev) => ({
        ...prev,
        [newRound]: { ...row, lotteryKey, aiSummary },
      }));
    } catch (err) {
      setDetailCache((prev) => ({ ...prev, [newRound]: null }));
    } finally {
      setLoadingRounds((prev) => ({ ...prev, [newRound]: false }));
    }
  };

  return (
    <div className="" title={title}>
      <Accordion
        type="multiple"
        value={openRounds}
        onValueChange={handleValueChange}
        className="grid gap-4"
      >
        {items.map((item, index) => (
          <React.Fragment key={item.uuid}>
            <AccordionItem
              value={String(item.round)}
              className="relative border border-primary-500 rounded-lg bg-base-50 w-full"
            >
              <div className="absolute -top-2 left-2 flex gap-1">
                {isNew(item.draw_date) && <Badge variant="destructive">NEW</Badge>}
                {item.carryover > 0 && <Badge variant="gold">CO</Badge>}
                {analyzedUuids.has(item.uuid) && <Badge variant="outline" className="bg-base-50 text-primary-500 border-primary-500 text-[10px] px-1 py-0">AI</Badge>}
              </div>
              <AccordionTrigger className="flex flex-col md:flex-row items-center px-4 py-4 gap-2 relative hover:no-underline [&>svg]:ml-auto [&>svg]:shrink-0 [&>svg]:absolute [&>svg]:right-2 [&>svg]:md:relative [&>svg]:text-primary-500">
                <div className="flex flex-row md:flex-col items-start items-baseline w-full md:w-auto gap-2 md:gap-0">
                  <span className="text-xs text-primary-500 font-bold">第 {item.round} 回</span>
                  <span className="text-sm text-primary-500">{item.draw_date}</span>
                </div>
                <div className="flex-1 flex justify-center w-full md:w-auto">
                  <LotteryNumlotWinningNumbers
                    mainNumber={item.main_number}
                    bonusNumber={item.bonus_number}
                    config={config}
                    size="sm"
                    fontSize={fontSize}
                  />
                </div>
              </AccordionTrigger>

              <AccordionContent className="px-4 pb-4">
                <LotteryNumlotIndexDetailTemplate
                  data={detailCache[String(item.round)]}
                  config={config}
                  loading={loadingRounds[String(item.round)] ?? false}
                />
                {/* AI解析サマリー */}
                {detailCache[String(item.round)]?.aiSummary && (
                  <div className="mb-3 p-3 border border-primary-500/30 rounded-lg bg-primary-500/5">
                    <p className="font-bold text-primary-500 mb-1">［ AI解析 ］</p>
                    <p className="text-primary-500/80">{detailCache[String(item.round)].aiSummary}</p>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
            {(index + 1) % 10 === 0 && (
              <AdBanner key={`ad-${index}`} />
            )}
          </React.Fragment>
        ))}
      </Accordion>

      {showMore && hasMore && (
        <div className="my-16 mx-auto max-w-sm">
          <button
            onClick={onMore}
            disabled={loading}
            className="w-full block py-2 text-primary-500 border border-primary-500 rounded-xl font-bold bg-base-50 hover:bg-primary-500 hover:text-base-50 transition-colors text-center"
          >
            {loading ? "読み込み中..." : "さらに表示"}
          </button>
        </div>
      )}
    </div>
  );
}
